import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import SEOHead, { SEOData } from './SEOHead';
import { getFallbackSEO } from '../../config/seoFallback';
import { isKnownSpaPath, normalizePath } from '../../config/knownRoutes';

interface SEOWrapperProps {
  children: React.ReactNode;
  customSEO?: Partial<SEOData>;
}

const NOT_FOUND_SEO: SEOData = {
  title: 'Page not found | Brown Tech',
  description:
    'This address is not a page on the Brown Tech website. Visit the homepage or contact the team.',
  noIndex: true,
};

const SEOWrapper: React.FC<SEOWrapperProps> = ({ children, customSEO }) => {
  const location = useLocation();

  const finalSEO: SEOData = useMemo(() => {
    const path = normalizePath(location.pathname);
    const fallback = getFallbackSEO(path);
    // /ai-city is a demo shell. It must not be indexed or canonicalised.
    const base: SEOData = path === '/ai-city'
      ? {
          title: fallback.title || 'AI City Demo | Brown Tech',
          description: fallback.description || '',
          noIndex: true,
        }
      : isKnownSpaPath(path)
      ? {
          title: fallback.title || 'Brown Tech',
          description: fallback.description || '',
          canonical: path,
          ogType: 'website',
        }
      : NOT_FOUND_SEO;

    return {
      ...base,
      ...customSEO,
    };
  }, [location.pathname, customSEO]);

  return (
    <>
      <SEOHead data={finalSEO} />
      {children}
    </>
  );
};

export default SEOWrapper;
